import React, { useState } from 'react';
import { Droplets, Music, CheckCircle2, AlertTriangle, Heart, Baby, Shield, Clock } from 'lucide-react';

// Image du lavage des mains
const HANDWASHING_IMAGE = "https://static.prod-images.emergentagent.com/jobs/5d22348a-efc5-4943-a2b2-c44a5e71b6ae/images/80ec56c38a0dd2db93cfbb2afcd2946bf4b3813e8ce21793563e5c6928f6c53c.png";

// Les 5 étapes du lavage des mains
const LAVAGE_MAINS_STEPS = [
  {
    id: 1,
    title: "1. Mouiller les mains",
    icon: Droplets,
    objectif: "Éliminer les salissures visibles et préparer la peau au savonnage",
    roleAP: [
      "Vérifier la température de l'eau",
      "Adapter la hauteur (lavabo adapté à l'enfant)",
      "Accompagner verbalement l'enfant"
    ],
    color: "#3B82F6"
  },
  {
    id: 2,
    title: "2. Savonner (30 secondes)",
    icon: Clock,
    objectif: "Éliminer les micro-organismes par action mécanique + chimique",
    notionTheorique: "Les bactéries se transmettent principalement par les mains. 30 secondes minimum sont recommandées.",
    astuce: "Utiliser une chanson ou comptine de 30 secondes !",
    color: "#10B981"
  },
  {
    id: 3,
    title: "3. Frotter (30 secondes)",
    icon: Shield,
    objectif: "C'est le frottement qui élimine les germes",
    zones: ["Paumes", "Dos des mains", "Entre les doigts", "Pouces", "Ongles"],
    important: "Ne pas oublier les pouces et le bout des doigts !",
    color: "#F59E0B"
  },
  {
    id: 4,
    title: "4. Rincer",
    icon: Droplets,
    objectif: "Éliminer le savon et les impuretés",
    technique: ["Eau claire", "Mouvement vers le bas", "Rinçage complet"],
    color: "#6366F1"
  },
  {
    id: 5,
    title: "5. Sécher",
    icon: CheckCircle2,
    objectif: "Mains bien sèches = moins de prolifération bactérienne",
    technique: ["Essuyage soigneux", "Serviette propre ou papier à usage unique", "Sécher entre les doigts"],
    color: "#EC4899"
  }
];

// Comptine du lavage des mains (30 secondes)
const COMPTINE = {
  title: "Comptine du lavage des mains",
  subtitle: "À chanter pendant le savonnage (~30 secondes)",
  paroles: [
    "🎵 Je me lave les mains, les mains,",
    "Je frotte bien, je frotte bien,",
    "Dessus, dessous, entre les doigts,",
    "Le savon fait des bulles pour moi !",
    "",
    "🎵 Les pouces aussi, les pouces aussi,",
    "Et les ongles, petit bout d'oui !",
    "Je rince à l'eau, je sèche bien,",
    "Mes mains sont propres, je suis malin ! 🎵"
  ]
};

const LavageMainsCarousel = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showComptine, setShowComptine] = useState(false);

  const step = LAVAGE_MAINS_STEPS[currentStep];
  const Icon = step.icon;

  return (
    <div style={{
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '20px',
      padding: '25px',
      maxWidth: '900px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)',
        borderRadius: '15px',
        padding: '20px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <Droplets size={32} color="white" />
          <div>
            <h2 style={{ color: 'white', margin: 0, fontSize: '22px' }}>Le Lavage des Mains</h2>
            <p style={{ color: 'rgba(255,255,255,0.9)', margin: 0, fontSize: '14px' }}>
              5 étapes essentielles en hygiène
            </p>
          </div>
        </div>
        <div style={{
          background: 'rgba(255,255,255,0.2)',
          padding: '8px 15px',
          borderRadius: '20px',
          color: 'white',
          fontWeight: '600'
        }}>
          Étape {currentStep + 1} / {LAVAGE_MAINS_STEPS.length}
        </div>
      </div>

      {/* Main content */}
      <div style={{ display: 'flex', gap: '25px', flexWrap: 'wrap' }}>
        {/* Left - Image */}
        <div style={{ flex: '1', minWidth: '280px' }}>
          <img 
            src={HANDWASHING_IMAGE}
            alt="Lavage des mains avec l'enfant"
            style={{
              width: '100%',
              borderRadius: '15px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
            }}
          />
          
          {/* Comptine button */}
          <button
            onClick={() => setShowComptine(!showComptine)}
            style={{
              width: '100%',
              marginTop: '15px',
              padding: '12px 20px',
              background: showComptine 
                ? 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)'
                : 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              fontWeight: '600',
              fontSize: '15px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              transition: 'transform 0.2s'
            }}
          >
            <Music size={20} />
            {showComptine ? 'Masquer la comptine' : 'Voir la comptine du lavage 🎵'}
          </button>
        </div>

        {/* Right - Step details */}
        <div style={{ flex: '1', minWidth: '300px' }}>
          {/* Step card */}
          <div style={{
            background: 'white',
            borderRadius: '15px',
            padding: '20px',
            marginBottom: '15px',
            borderLeft: `4px solid ${step.color}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '15px' }}>
              <div style={{
                width: '45px',
                height: '45px',
                borderRadius: '12px',
                background: `${step.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Icon size={24} color={step.color} />
              </div>
              <h3 style={{ margin: 0, color: step.color, fontSize: '20px' }}>{step.title}</h3>
            </div>

            <div style={{
              background: `${step.color}10`,
              padding: '12px 15px',
              borderRadius: '10px',
              marginBottom: '12px'
            }}>
              <strong style={{ color: step.color }}>Objectif :</strong>
              <p style={{ margin: '5px 0 0', color: '#333' }}>{step.objectif}</p>
            </div>

            {step.roleAP && (
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: '#666', fontSize: '14px' }}>Rôle de l'AP :</strong>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {step.roleAP.map((item, idx) => (
                    <li key={idx} style={{ color: '#555', marginBottom: '4px' }}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {step.notionTheorique && (
              <div style={{
                background: '#FEF3C7',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '12px'
              }}>
                <AlertTriangle size={16} color="#D97706" style={{ marginRight: '8px' }} />
                <span style={{ color: '#92400E', fontSize: '14px' }}>{step.notionTheorique}</span>
              </div>
            )}

            {step.zones && (
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: '#666', fontSize: '14px' }}>Zones à ne pas oublier :</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                  {step.zones.map((zone, idx) => (
                    <span key={idx} style={{
                      background: `${step.color}15`,
                      color: step.color,
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}>
                      {zone}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {step.technique && (
              <div>
                <strong style={{ color: '#666', fontSize: '14px' }}>Technique :</strong>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {step.technique.map((item, idx) => (
                    <li key={idx} style={{ color: '#555', marginBottom: '4px' }}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {step.astuce && (
              <div style={{
                background: '#ECFDF5',
                padding: '12px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <span style={{ fontSize: '20px' }}>💡</span>
                <span style={{ color: '#065F46', fontWeight: '500' }}>{step.astuce}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Comptine display */}
      {showComptine && (
        <div style={{
          background: 'linear-gradient(135deg, #FDF4FF 0%, #FAE8FF 100%)',
          borderRadius: '15px',
          padding: '25px',
          marginTop: '20px',
          border: '2px solid #E879F9'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '15px' }}>
            <Music size={28} color="#A855F7" />
            <div>
              <h3 style={{ margin: 0, color: '#7C3AED' }}>{COMPTINE.title}</h3>
              <p style={{ margin: 0, color: '#9333EA', fontSize: '14px' }}>{COMPTINE.subtitle}</p>
            </div>
          </div>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            fontFamily: "'Comic Sans MS', cursive, sans-serif",
            fontSize: '16px',
            lineHeight: '2',
            color: '#4C1D95'
          }}>
            {COMPTINE.paroles.map((line, idx) => (
              <div key={idx}>{line}</div>
            ))}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '10px',
        marginTop: '20px'
      }}>
        {LAVAGE_MAINS_STEPS.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentStep(idx)}
            style={{
              width: idx === currentStep ? '35px' : '12px',
              height: '12px',
              borderRadius: '6px',
              border: 'none',
              background: idx === currentStep 
                ? LAVAGE_MAINS_STEPS[idx].color 
                : '#CBD5E1',
              cursor: 'pointer',
              transition: 'all 0.3s'
            }}
          />
        ))}
      </div>

      {/* Navigation buttons */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '20px'
      }}>
        <button
          onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
          disabled={currentStep === 0}
          style={{
            padding: '10px 25px',
            background: currentStep === 0 ? '#E5E7EB' : '#0891B2',
            color: currentStep === 0 ? '#9CA3AF' : 'white',
            border: 'none',
            borderRadius: '10px',
            fontWeight: '600',
            cursor: currentStep === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          ← Précédent
        </button>
        <button
          onClick={() => setCurrentStep(Math.min(LAVAGE_MAINS_STEPS.length - 1, currentStep + 1))}
          disabled={currentStep === LAVAGE_MAINS_STEPS.length - 1}
          style={{
            padding: '10px 25px',
            background: currentStep === LAVAGE_MAINS_STEPS.length - 1 ? '#E5E7EB' : '#0891B2',
            color: currentStep === LAVAGE_MAINS_STEPS.length - 1 ? '#9CA3AF' : 'white',
            border: 'none',
            borderRadius: '10px',
            fontWeight: '600',
            cursor: currentStep === LAVAGE_MAINS_STEPS.length - 1 ? 'not-allowed' : 'pointer'
          }}
        >
          Suivant →
        </button>
      </div>
    </div>
  );
};

export default LavageMainsCarousel;
